/**
 * Hook to calculate split point for past/future predictions
 *
 * Calculates:
 * - lastRealEstimate: The last "real" estimate (non-zero, <= current month)
 * - predictionSplitDay: The day to split past/future predictions
 *
 * This is used to ensure future predictions start from the last real estimate value.
 */

import { useMemo } from 'react';
import type { UnifiedForecastResult, RebatePoint } from '@erp/shared-types';

/**
 * Calculate the last "real" estimate and split point
 *
 * @param forecastData - The forecast data (should be merged data)
 * @param currentDate - The current date to use for calculations (defaults to now)
 * @param isHistoricalYear - Whether viewing a completed historical year (all data should be "past")
 * @returns Object with lastRealEstimate and predictionSplitDay
 */
export function useSplitPoint(
  forecastData: UnifiedForecastResult | null | undefined,
  currentDate?: Date,
  isHistoricalYear = false,
): {
  lastRealEstimate: RebatePoint | null;
  predictionSplitDay: number;
  reportedSplitDay: number;
} {
  return useMemo(() => {
    // Use provided current date or default to now
    const now = currentDate || new Date();

    // Calculate period start (used throughout for daysFromStart calculations)
    const periodStart =
      forecastData?.forecastPeriod?.startDate ||
      (forecastData?.estimates && forecastData.estimates.length > 0
        ? forecastData.estimates[0].date
        : new Date(now.getUTCFullYear(), 0, 1));

    // Reported split is always based on today's date:
    // reported data for dates that have passed = black (past)
    // reported data for dates in the future = blue (future)
    const todayAsDaysFromStart = Math.ceil(
      (now.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24),
    );

    // For historical years, set split days beyond all data so everything is "past"
    // This ensures reported data is black and predictions are magenta (not blue)
    if (isHistoricalYear) {
      // Find the last estimate if available
      const lastEstimate = forecastData?.estimates?.length
        ? [...forecastData.estimates].sort(
            (a, b) => b.daysFromStart - a.daysFromStart,
          )[0]
        : null;

      // Use period end or a large value to ensure all data is "past"
      const periodEnd = forecastData?.forecastPeriod?.endDate;
      let splitDay = 366; // Default: beyond a full year

      if (periodEnd && periodStart) {
        // Calculate days in the period + 1 to be beyond all data
        splitDay =
          Math.ceil(
            (periodEnd.getTime() - periodStart.getTime()) /
              (1000 * 60 * 60 * 24),
          ) + 1;
      }

      return {
        lastRealEstimate: lastEstimate,
        predictionSplitDay: splitDay,
        reportedSplitDay: splitDay,
      };
    }

    if (!forecastData?.estimates || forecastData.estimates.length === 0) {
      // No estimates: prediction split is 0 but reported split is today
      return {
        lastRealEstimate: null,
        predictionSplitDay: 0,
        reportedSplitDay: todayAsDaysFromStart,
      };
    }

    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth(); // 0-indexed (0 = January, 11 = December)

    // Get the last day of the current month
    const currentMonthEndDate = new Date(
      Date.UTC(currentYear, currentMonth + 1, 0, 23, 59, 59, 999),
    );

    // Calculate daysFromStart for current month end
    const currentMonthEndDay = Math.ceil(
      (currentMonthEndDate.getTime() - periodStart.getTime()) /
        (1000 * 60 * 60 * 24),
    );

    // Check if we have estimates for the current month
    const hasCurrentMonthEstimates = forecastData.estimates.some((e) => {
      const estimateYear = e.date.getUTCFullYear();
      const estimateMonth = e.date.getUTCMonth();
      return (
        estimateYear === currentYear &&
        estimateMonth === currentMonth &&
        Math.abs(e.totalRebateAmount) >= 0.01
      );
    });

    // Find the last "real" estimate: non-zero value that is <= current month end
    // Backend provides estimates for all months, but only those up to current month
    // with non-zero values are considered "real"
    const lastReal = [...forecastData.estimates]
      .filter((e) => {
        // Must be non-zero
        if (Math.abs(e.totalRebateAmount) < 0.01) return false;
        // Must be at or before current month end
        return Number(e.daysFromStart) <= currentMonthEndDay;
      })
      .sort((a, b) => a.daysFromStart - b.daysFromStart)
      .pop(); // Get the last one

    // Determine the prediction split point:
    // - If we have current month estimates, split at current month end
    // - Otherwise, split at previous month end (not at last estimate date)
    // - NEVER exceed today's date (can't show "future" predictions for past dates)
    let predictionSplitDay = 0;
    if (hasCurrentMonthEstimates) {
      predictionSplitDay = currentMonthEndDay;
    } else if (currentMonth > 0) {
      // Previous month end (month before current month)
      const previousMonthEndDate = new Date(
        Date.UTC(currentYear, currentMonth, 0, 23, 59, 59, 999),
      );
      predictionSplitDay = Math.ceil(
        (previousMonthEndDate.getTime() - periodStart.getTime()) /
          (1000 * 60 * 60 * 24),
      );
    } else if (lastReal) {
      // Fallback: if we're in January and no estimates, use last estimate date
      predictionSplitDay = lastReal.daysFromStart;
    }

    // Ensure predictionSplitDay is at least today
    predictionSplitDay = Math.max(predictionSplitDay, todayAsDaysFromStart);

    return {
      lastRealEstimate: lastReal || null,
      predictionSplitDay,
      // Reported split is always today: past dates = black, future dates = blue
      reportedSplitDay: todayAsDaysFromStart,
    };
  }, [forecastData, currentDate, isHistoricalYear]);
}
